
import { LeadContact } from "@/services/leadContactsService";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CalendarIcon,
  Edit,
  ExternalLink,
  Globe,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Twitter,
  Building,
  Briefcase,
  BarChart3,
  MessageCircle,
  Star,
  Github,
  Facebook,
  Instagram,
  Link,
  Info,
  Trash2,
  FileText,
  User as UserIcon
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CallConfirmationDialog } from "@/components/contacts/CallConfirmationDialog";
import { useTwilioPermissions } from "@/hooks/useTwilioPermissions";
import { extractPrimaryPhone } from "@/utils/contactDataHelpers";
import { useUsers } from "@/hooks/useUsers";

interface ContactDetailProps {
  contact: LeadContact;
  onEdit?: (contact: LeadContact) => void;
  onBack?: () => void;
  onInitiateCall?: (contact: LeadContact) => void;
  onDelete?: () => void;
}

const ContactDetail = ({ contact, onEdit, onBack, onInitiateCall, onDelete }: ContactDetailProps) => {
  console.log('🚀 ContactDetail: Component loaded with contact:', contact);
  
  const navigate = useNavigate();
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const { micPermission, requestPermissions } = useTwilioPermissions();
  const { users } = useUsers();

  const handleCreateProposal = () => {
    navigate(`/app/proposals/create/${contact.id}`);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleCallClick = (phoneNumber?: string) => {
    const phone = phoneNumber || getPrimaryPhone(contact);
    console.log('📞 ContactDetail: Attempting to call phone:', phone);
    if (phone) {
      if (onInitiateCall) {
        // Use Twilio calling with confirmation dialog
        setIsCallDialogOpen(true);
      } else {
        // Fallback to tel: link if no onInitiateCall provided
        window.open(`tel:${phone}`, '_self');
      }
    }
  };

  const handleConfirmCall = async () => {
    if (micPermission === 'prompt') {
      await requestPermissions();
    }
    
    setIsInitiatingCall(true);
    
    try {
      if (onInitiateCall) {
        await onInitiateCall(contact);
      }
    } catch (error) {
      console.error('Failed to initiate call:', error);
    } finally {
      setIsInitiatingCall(false);
      setIsCallDialogOpen(false);
    }
  };

  const handleEmail = (emailAddress?: string) => {
    const email = emailAddress || getPrimaryEmail(contact);
    console.log('📧 ContactDetail: Attempting to email:', email);
    if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  };

  // Helper function to get icon for URL type
  const getUrlIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'linkedin':
        return <Linkedin className="h-4 w-4" />;
      case 'twitter':
        return <Twitter className="h-4 w-4" />;
      case 'github':
        return <Github className="h-4 w-4" />;
      case 'facebook':
        return <Facebook className="h-4 w-4" />;
      case 'instagram':
        return <Instagram className="h-4 w-4" />;
      case 'website':
        return <Globe className="h-4 w-4" />;
      default:
        return <Link className="h-4 w-4" />;
    }
  };

  // Helper function to parse URLs object
  const parseUrls = (urls: any) => {
    if (!urls) return {};
    if (typeof urls === 'string') {
      try {
        return JSON.parse(urls);
      } catch {
        return {};
      }
    }
    return urls;
  };

  // Helper function to parse metadata object
  const parseMetadata = (metadata: any) => {
    if (!metadata) return {};
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return metadata;
  };

  // Helper function to parse phone data
  const parsePhones = (phones: any) => {
    console.log('📞 ContactDetail: Parsing phones data:', phones);
    if (!phones) return [];
    
    if (typeof phones === 'string') {
      try {
        const parsed = JSON.parse(phones);
        console.log('📞 ContactDetail: Parsed phone string to:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        console.log('📞 ContactDetail: Failed to parse phone string, returning empty array');
        return [];
      }
    }
    
    if (Array.isArray(phones)) {
      console.log('📞 ContactDetail: Phones already array:', phones);
      return phones;
    }
    
    console.log('📞 ContactDetail: Unknown phone format, returning empty array');
    return [];
  };

  // Helper function to parse email data
  const parseEmails = (emails: any) => {
    console.log('📧 ContactDetail: Parsing emails data:', emails);
    if (!emails) return [];
    
    if (typeof emails === 'string') {
      try {
        const parsed = JSON.parse(emails);
        console.log('📧 ContactDetail: Parsed email string to:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        console.log('📧 ContactDetail: Failed to parse email string, returning empty array');
        return [];
      }
    }
    
    if (Array.isArray(emails)) {
      console.log('📧 ContactDetail: Emails already array:', emails);
      return emails;
    }
    
    console.log('📧 ContactDetail: Unknown email format, returning empty array');
    return [];
  };

  // Helper function to get primary phone
  const getPrimaryPhone = (contact: LeadContact) => {
    const phones = parsePhones(contact.phones);
    if (phones.length > 0) {
      const primary = phones.find(p => p.isPrimary || p.is_primary);
      return primary?.phone || phones[0]?.phone;
    }
    return contact.phone_number || extractPrimaryPhone(contact.phone) || null;
  };

  // Helper function to get primary email
  const getPrimaryEmail = (contact: LeadContact) => {
    const emails = parseEmails(contact.emails);
    if (emails.length > 0) {
      const primary = emails.find(e => e.isPrimary || e.is_primary);
      return primary?.email || emails[0]?.email;
    }
    return typeof contact.email === 'string' ? contact.email : null;
  };

  // Helper function to format URL
  const formatUrl = (url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const parsedUrls = parseUrls(contact.urls);
  const parsedMetadata = parseMetadata(contact.metadata);
  const parsedPhones = parsePhones(contact.phones);
  const parsedEmails = parseEmails(contact.emails);

  // Drop blank values (empty strings, arrays, objects) and order star/type first
  const isBlank = (value: any) => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  };
  const metadataPriority = ['star', 'starred', 'is_star', 'type'];
  const orderedMetadata = Object.entries(parsedMetadata)
    .filter(([key, value]) => key !== 'created_by' && !isBlank(value))
    .sort(([a], [b]) => {
      const ai = metadataPriority.indexOf(a);
      const bi = metadataPriority.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

  const createdById = (contact as any).created_by ?? parsedMetadata?.created_by;
  const creator = users.find((u) => u.id === Number(createdById));
  const createdByName = creator
    ? `${creator.first_name} ${creator.last_name}`.trim() || creator.username
    : createdById
      ? `User #${createdById}`
      : null;


  console.log('🔍 ContactDetail: Contact data structure:', {
    contact: contact,
    parsedPhones: parsedPhones,
    parsedEmails: parsedEmails,
    primaryPhone: getPrimaryPhone(contact),
    primaryEmail: getPrimaryEmail(contact)
  });

  return (
    <>
      <div className="mx-auto space-y-6">
        {/* Edit button moved to main card header */}
        
        {/* Main contact info card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b pb-6">
            <div className="flex justify-between items-start">
              <div className="flex gap-4 items-center">
                <Avatar className="h-20 w-20 border">
                  <AvatarImage src={contact.avatar_url || undefined} />
                  <AvatarFallback className="text-2xl">
                    {getInitials(contact.first_name, contact.last_name)}
                  </AvatarFallback>
                </Avatar>
                
                <div>
                  <div className="flex gap-2 items-center">
                    <CardTitle className="text-2xl">
                      {contact.first_name} {contact.last_name}
                    </CardTitle>
                    {(contact.decision_maker || contact.is_decision_maker) && (
                      <Badge className="bg-yellow-500">
                        <Star className="h-3 w-3 mr-1" /> Decision Maker
                      </Badge>
                    )}
                  </div>
                  
                  <CardDescription className="mt-1 text-base">
                    {contact.designation || "No designation"}
                    {contact.department && ` · ${contact.department}`}
                  </CardDescription>
                  
                  {contact.company && (
                    <div className="flex items-center gap-1 mt-1">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span>{contact.company.name}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Quick action buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handleCreateProposal}
                  title="Create Proposal"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create Proposal
                </Button>
                {getPrimaryEmail(contact) && (
                  <Button variant="outline" size="icon" onClick={() => handleEmail()} title="Send email">
                    <Mail className="h-4 w-4" />
                  </Button>
                )}
                {getPrimaryPhone(contact) && (
                  <Button variant="outline" size="icon" onClick={() => handleCallClick()} title="Call">
                    <Phone className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="outline" size="icon" title="Log interaction">
                  <MessageCircle className="h-4 w-4" />
                </Button>
                {onEdit && (
                  <Button variant="outline" size="icon" onClick={() => onEdit(contact)} title="Edit Contact">
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {onDelete && (
                  <Button variant="outline" size="icon" onClick={onDelete} title="Delete Contact" className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {/* Personal information */}
              <div>
                <h3 className="text-lg font-medium mb-4">Contact Information</h3>
                <dl className="space-y-4">
                   {/* Multiple Emails Section */}
                   {parsedEmails.length > 0 ? (
                     <div className="space-y-4">
                       <dt className="text-sm font-semibold text-foreground flex items-center">
                         <Mail className="h-4 w-4 mr-3 text-muted-foreground" />
                         Email{parsedEmails.length > 1 ? 's' : ''}
                       </dt>
                       <div className="space-y-3">
                         {parsedEmails.map((emailObj, index) => (
                           <dd key={index} className="ml-7">
                             <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                               <a 
                                 href={`mailto:${emailObj.email}`} 
                                 className="text-primary hover:underline font-medium flex-1 mr-3"
                                 onClick={(e) => {
                                   e.preventDefault();
                                   handleEmail(emailObj.email);
                                 }}
                               >
                                 {emailObj.email}
                               </a>
                               <div className="flex items-center gap-2">
                                 {emailObj.type && (
                                   <Badge variant="secondary" className="text-xs font-medium h-6 px-2">
                                     {emailObj.type}
                                   </Badge>
                                 )}
                                 {(emailObj.isPrimary || emailObj.is_primary) && (
                                   <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 border border-amber-200" title="Primary Email">
                                     <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                                   </div>
                                 )}
                               </div>
                             </div>
                           </dd>
                         ))}
                       </div>
                     </div>
                  ) : getPrimaryEmail(contact) && (
                    <div className="flex items-center gap-3">
                      <dt className="text-muted-foreground flex items-center">
                        <Mail className="h-4 w-4 mr-2" />
                        Email:
                      </dt>
                      <dd>
                        <a 
                          href={`mailto:${getPrimaryEmail(contact)}`} 
                          className="text-primary hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            handleEmail(getPrimaryEmail(contact) || undefined);
                          }}
                        >
                          {getPrimaryEmail(contact)}
                        </a>
                      </dd>
                    </div>
                  )}
                  
                   {/* Multiple Phones Section */}
                   {parsedPhones.length > 0 ? (
                     <div className="space-y-4">
                       <dt className="text-sm font-semibold text-foreground flex items-center">
                         <Phone className="h-4 w-4 mr-3 text-muted-foreground" />
                         Phone{parsedPhones.length > 1 ? 's' : ''}
                       </dt>
                       <div className="space-y-3">
                         {parsedPhones.map((phoneObj, index) => (
                           <dd key={index} className="ml-7">
                             <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                               <button 
                                 onClick={() => handleCallClick(phoneObj.phone)}
                                 className="text-primary hover:underline cursor-pointer font-medium flex-1 text-left mr-3"
                               >
                                 {phoneObj.phone}
                               </button>
                               <div className="flex items-center gap-2">
                                 {phoneObj.type && (
                                   <Badge variant="secondary" className="text-xs font-medium h-6 px-2">
                                     {phoneObj.type}
                                   </Badge>
                                 )}
                                 {(phoneObj.isPrimary || phoneObj.is_primary) && (
                                   <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 border border-amber-200" title="Primary Phone">
                                     <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                                   </div>
                                 )}
                               </div>
                             </div>
                           </dd>
                         ))}
                       </div>
                        </div>
                   ) : getPrimaryPhone(contact) && (
                    <div className="flex items-center gap-3">
                      <dt className="text-muted-foreground flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        Phone:
                      </dt>
                      <dd>
                        <button 
                          onClick={() => handleCallClick()}
                          className="text-primary hover:underline cursor-pointer"
                        >
                          {getPrimaryPhone(contact)}
                        </button>
                      </dd>
                    </div>
                  )}
                  
                  {contact.linkedin_url && (
                    <div className="flex items-center gap-3">
                      <dt className="text-muted-foreground flex items-center">
                        <Linkedin className="h-4 w-4 mr-2" />
                        LinkedIn:
                      </dt>
                      <dd>
                        <a 
                          href={contact.linkedin_url.startsWith('http') ? contact.linkedin_url : `https://${contact.linkedin_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center"
                        >
                          Profile <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </dd>
                    </div>
                  )}

                  {(contact.address || contact.city || contact.state || contact.country) && (
                    <div className="flex items-center gap-3">
                      <dt className="text-muted-foreground flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        Location:
                      </dt>
                      <dd>
                        {[
                          contact.address,
                          contact.city,
                          contact.state,
                          contact.country
                        ].filter(Boolean).join(", ")}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
              
              {/* Company information */}
              {contact.company && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Company Information</h3>
                  <dl className="space-y-4">
                    <div className="flex items-center gap-3">
                      <dt className="text-muted-foreground flex items-center">
                        <Building className="h-4 w-4 mr-2" />
                        Company:
                      </dt>
                      <dd>{contact.company.name}</dd>
                    </div>
                    
                    {contact.designation && (
                      <div className="flex items-center gap-3">
                        <dt className="text-muted-foreground flex items-center">
                          <Briefcase className="h-4 w-4 mr-2" />
                          Position:
                        </dt>
                        <dd>{contact.designation}</dd>
                      </div>
                    )}
                    
                    {contact.company.website_url && (
                      <div className="flex items-center gap-3">
                        <dt className="text-muted-foreground flex items-center">
                          <Globe className="h-4 w-4 mr-2" />
                          Website:
                        </dt>
                        <dd>
                          <a 
                            href={contact.company.website_url.startsWith('http') ? contact.company.website_url : `https://${contact.company.website_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center"
                          >
                            {contact.company.website_url.replace(/^https?:\/\//, '')} 
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </dd>
                      </div>
                    )}
                    
                    {contact.company.industry && (
                      <div className="flex items-center gap-3">
                        <dt className="text-muted-foreground flex items-center">
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Industry:
                        </dt>
                        <dd>{contact.company.industry}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
            
            <Separator className="my-6" />
            
            {/* Engagement section */}
            <div>
              <h3 className="text-lg font-medium mb-4">Engagement</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {contact.influence_score && (
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-muted-foreground text-sm">Influence Score</p>
                      <p className="text-2xl font-bold">{contact.influence_score}/10</p>
                    </CardContent>
                  </Card>
                )}
                
                {contact.engagement_score && (
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-muted-foreground text-sm">Engagement Score</p>
                      <p className="text-2xl font-bold">{Math.round(contact.engagement_score * 100)}%</p>
                    </CardContent>
                  </Card>
                )}
                
                {contact.lead_score && (
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-muted-foreground text-sm">Lead Score</p>
                      <p className="text-2xl font-bold">{contact.lead_score}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
            
            {/* URLs Section */}
            {Object.keys(parsedUrls).length > 0 && (
              <>
                <Separator className="my-6" />
                <div>
                  <h3 className="text-lg font-medium mb-4">Social & Links</h3>
                  <dl className="space-y-4">
                    {Object.entries(parsedUrls).map(([type, url]) => (
                      <div key={type} className="flex items-center gap-3">
                        <dt className="text-muted-foreground flex items-center">
                          {getUrlIcon(type)}
                          <span className="ml-2 capitalize">{type}:</span>
                        </dt>
                        <dd>
                          <a 
                            href={formatUrl(url as string)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center"
                          >
                            {type === 'website' ? (url as string).replace(/^https?:\/\//, '') : 'View Profile'}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </>
            )}
            
            {/* Metadata Section */}
            {orderedMetadata.length > 0 && (
              <>
                <Separator className="my-6" />
                <div>
                  <h3 className="text-lg font-medium mb-4">Additional Information</h3>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {orderedMetadata.map(([key, value]) => (
                      <div key={key} className="flex items-start gap-3">
                        <dt className="text-muted-foreground flex items-center min-w-0">
                          <Info className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="capitalize text-sm">
                            {key.replace(/_/g, ' ')}:
                          </span>
                        </dt>
                        <dd className="text-sm break-words min-w-0 flex-1">
                          {Array.isArray(value)
                            ? value.join(', ')
                            : typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </>
            )}
          </CardContent>
          
          <CardFooter className="border-t pt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-3.5 w-3.5" />
              {contact.created_at ? (
                <span>Created {format(new Date(contact.created_at), "MMM d, yyyy")}</span>
              ) : (
                <span>No creation date</span>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-3.5 w-3.5" />
              {contact.updated_at ? (
                <span>Updated {format(new Date(contact.updated_at), "MMM d, yyyy")}</span>
              ) : (
                <span>No update date</span>
              )}
            </div>

            {createdByName && (
              <div className="flex items-center gap-1">
                <UserIcon className="h-3.5 w-3.5" />
                <span>Created by {createdByName}</span>
              </div>
            )}
          </CardFooter>

        </Card>
      </div>

      <CallConfirmationDialog
        isOpen={isCallDialogOpen}
        onClose={() => setIsCallDialogOpen(false)}
        onConfirmCall={handleConfirmCall}
        contact={contact}
        isInitiating={isInitiatingCall}
        micPermission={micPermission}
      />
    </>
  );
};

export default ContactDetail;
